// src/pages/Signup.jsx
// Premium SaaS signup experience

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Check,
  Shield,
  Eye,
  EyeOff,
  Loader2,
  X,
  CheckCircle2,
  ArrowRight,
  Users,
  BarChart3,
  Zap,
  Lock,
  User,
  Building2,
} from "lucide-react";
import PasswordStrengthMeter, { validatePasswordStrength } from "../components/PasswordStrengthMeter";

const brand = {
  navy: "#172A3A",
  slate: "#20344A",
  gold: "#FFB800",
  gradFrom: "#1f3550",
  gradTo: "#1672d6",
};

const AGENT_SIZE_OPTIONS = [
  { label: "Just me", value: 1, sub: "Solo agent" },
  { label: "2 – 5", value: 3, sub: "Small team" },
  { label: "6 – 20", value: 10, sub: "Growing agency" },
  { label: "21 – 50", value: 30, sub: "Mid-size agency" },
  { label: "50+", value: 51, sub: "Enterprise" },
];

const Signup = () => {
  const [accountType, setAccountType] = useState(null); // 'agent' | 'agency'
  const [formData, setFormData] = useState({
    name: "",
    companyName: "",
    agencyName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [agentCount, setAgentCount] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError("");
  };

  const validateForm = () => {
    if (!accountType) {
      setError("Please select whether you are an independent agent or an agency");
      return false;
    }
    if (!formData.name.trim()) {
      setError("Please enter your full name");
      return false;
    }
    if (accountType === "agency") {
      if (!formData.agencyName.trim()) {
        setError("Please enter your agency name");
        return false;
      }
      if (!agentCount) {
        setError("Please select how many agents are in your agency");
        return false;
      }
    }
    if (!formData.email.trim()) {
      setError("Please enter your email address");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Please enter a valid email address");
      return false;
    }
    const passwordError = validatePasswordStrength(formData.password, formData.email);
    if (passwordError) {
      setError(passwordError);
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setError("");

    try {
      const BASE = process.env.REACT_APP_API_URL || "";
      const body =
        accountType === "agency"
          ? {
              email: formData.email,
              password: formData.password,
              name: formData.name,
              agencyName: formData.agencyName,
              agentCount,
              role: "agency_owner",
            }
          : {
              email: formData.email,
              password: formData.password,
              name: formData.name,
              companyName: formData.companyName || undefined,
              role: "agent",
            };

      const res = await fetch(`${BASE}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Signup failed. Please try again.");
      }

      authLogin(data.user || null);
      navigate("/onboarding");
    } catch (e) {
      setError(e.message || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: Users, text: "Unlimited Medicare clients" },
    { icon: BarChart3, text: "AI-powered churn prediction" },
    { icon: Zap, text: "CMS Blue Button integration" },
    { icon: Shield, text: "HIPAA-compliant & secure" },
  ];

  return (
    <div className="min-h-screen flex" style={{ background: `linear-gradient(135deg, ${brand.gradFrom} 0%, ${brand.gradTo} 100%)` }}>
      {/* Left side – Benefits */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center px-12 xl:px-20">
        <Link to="/" className="flex items-center gap-3 mb-8">
          <img src="/logo.png" alt="Pro IRP" className="w-12 h-12 rounded-full bg-white shadow" />
          <span className="font-extrabold text-white text-2xl">
            Pro <span style={{ color: brand.gold }}>IRP</span>
          </span>
        </Link>

        <h1 className="text-4xl font-extrabold text-white leading-tight">
          Start protecting your<br />
          <span style={{ color: brand.gold }}>book of business</span>
        </h1>

        <p className="mt-4 text-lg text-white/80">
          Join 500+ Medicare agents who use Pro IRP to predict churn, automate outreach, and keep more clients.
        </p>

        <div className="mt-8 space-y-4">
          {benefits.map((benefit, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center bg-white/10 backdrop-blur">
                <benefit.icon size={20} style={{ color: brand.gold }} />
              </div>
              <span className="text-white text-lg">{benefit.text}</span>
            </div>
          ))}
        </div>

        <div className="mt-10 p-4 rounded-2xl bg-white/5 backdrop-blur border border-white/10">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex -space-x-2">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 border-2 border-white/20 flex items-center justify-center text-white text-xs font-bold">
                  {['M', 'S', 'D', 'J'][i - 1]}
                </div>
              ))}
            </div>
            <div className="flex text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
          </div>
          <p className="text-white/70 text-sm">
            "Pro IRP saved me from losing 23 clients last AEP. The ROI is insane."
            <span className="block text-white/50 mt-1">— Michael R., Independent Agent</span>
          </p>
        </div>

        <div className="mt-8 flex items-center gap-3 text-white/60 text-sm">
          <Lock size={16} />
          <span>256-bit encryption &bull; SOC 2 compliant &bull; HIPAA secure</span>
        </div>
      </div>

      {/* Right side – Signup Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10 w-full max-w-md" style={{ borderTop: `8px solid ${brand.gold}` }}>
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-6">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.png" alt="Pro IRP" className="w-10 h-10 rounded-full bg-white shadow" />
              <span className="font-extrabold text-xl" style={{ color: brand.navy }}>
                Pro <span style={{ color: brand.gold }}>IRP</span>
              </span>
            </Link>
          </div>

          <div className="text-center lg:text-left">
            <h2 className="text-2xl font-extrabold" style={{ color: brand.navy }}>
              Create your account
            </h2>
            <p className="mt-1 text-sm" style={{ color: brand.slate }}>
              Free to get started &bull; Full access
            </p>
          </div>

          {error && (
            <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
              <X className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">

            {/* ── Account Type Picker ── */}
            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: brand.navy }}>
                Step 1: Choose your account type
              </label>
              <p className="text-xs text-gray-500 mb-2">This determines which features and dashboard you'll have access to.</p>
              <div className="grid grid-cols-2 gap-3">
                {/* Independent Agent */}
                <button
                  type="button"
                  onClick={() => { setAccountType("agent"); setError(""); }}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition cursor-pointer text-left ${
                    accountType === "agent"
                      ? "border-yellow-400 bg-yellow-50"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${accountType === "agent" ? "bg-yellow-100" : "bg-gray-100"}`}>
                    <User size={20} style={{ color: accountType === "agent" ? brand.gold : "#6b7280" }} />
                  </div>
                  <div>
                    <p className="font-semibold text-sm" style={{ color: brand.navy }}>Independent Agent</p>
                    <p className="text-xs text-gray-500 mt-0.5">Solo or small practice</p>
                  </div>
                  {accountType === "agent" && (
                    <div className="absolute top-2 right-2">
                      <Check size={14} className="text-yellow-500" />
                    </div>
                  )}
                </button>

                {/* Agency Owner */}
                <button
                  type="button"
                  onClick={() => { setAccountType("agency"); setError(""); }}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition cursor-pointer text-left ${
                    accountType === "agency"
                      ? "border-yellow-400 bg-yellow-50"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${accountType === "agency" ? "bg-yellow-100" : "bg-gray-100"}`}>
                    <Building2 size={20} style={{ color: accountType === "agency" ? brand.gold : "#6b7280" }} />
                  </div>
                  <div>
                    <p className="font-semibold text-sm" style={{ color: brand.navy }}>Agency Owner</p>
                    <p className="text-xs text-gray-500 mt-0.5">Manage a team of agents</p>
                  </div>
                </button>
              </div>
            </div>

            {/* ── Agency-specific fields ── */}
            {accountType === "agency" && (
              <>
                <div>
                  <label className="block text-sm font-semibold mb-1" style={{ color: brand.navy }}>
                    Agency Name
                  </label>
                  <input
                    type="text"
                    name="agencyName"
                    value={formData.agencyName}
                    onChange={handleChange}
                    placeholder="e.g. Smith Medicare Solutions"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: brand.navy }}>
                    How many agents are in your agency?
                  </label>
                  <div className="grid grid-cols-5 gap-1.5">
                    {AGENT_SIZE_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setAgentCount(opt.value)}
                        className={`flex flex-col items-center py-2 px-1 rounded-xl border-2 text-center transition ${
                          agentCount === opt.value
                            ? "border-yellow-400 bg-yellow-50"
                            : "border-gray-200 hover:border-gray-300 bg-white"
                        }`}
                      >
                        <span className="font-bold text-xs" style={{ color: brand.navy }}>{opt.label}</span>
                        <span className="text-gray-400 text-[10px] leading-tight mt-0.5">{opt.sub}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* ── Agent-specific optional field ── */}
            {accountType === "agent" && (
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: brand.navy }}>
                  Company / Agency <span className="font-normal text-gray-500">(optional)</span>
                </label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="Your Agency Name"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition"
                />
              </div>
            )}

            {/* ── Common fields (shown once type is selected) ── */}
            {accountType && (
              <>
                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold mb-1" style={{ color: brand.navy }}>
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition"
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold mb-1" style={{ color: brand.navy }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    autoComplete="email"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition"
                    required
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-semibold mb-1" style={{ color: brand.navy }}>
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create a strong password"
                      autoComplete="new-password"
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition pr-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  <PasswordStrengthMeter password={formData.password} email={formData.email} showRequirements={true} className="mt-2" />
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-semibold mb-1" style={{ color: brand.navy }}>
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm your password"
                      autoComplete="new-password"
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition pr-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                    <p className="text-red-500 text-xs mt-1">Passwords don't match</p>
                  )}
                  {formData.confirmPassword && formData.password === formData.confirmPassword && formData.password.length > 0 && (
                    <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                      <CheckCircle2 size={12} /> Passwords match
                    </p>
                  )}
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 rounded-xl font-bold text-lg shadow-lg transition hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 mt-2"
                  style={{ background: brand.gold, color: brand.navy }}
                >
                  {loading ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    <>
                      {accountType === "agency" ? "Create Agency Account" : "Create Account"}
                      <ArrowRight size={20} />
                    </>
                  )}
                </button>

                {/* Terms */}
                <p className="text-xs text-center" style={{ color: brand.slate }}>
                  By creating an account, you agree to our{" "}
                  <Link to="/terms" className="underline hover:no-underline">Terms of Service</Link>
                  {" "}and{" "}
                  <Link to="/privacy" className="underline hover:no-underline">Privacy Policy</Link>
                </p>
              </>
            )}
          </form>

          {/* Login Link */}
          <div className="mt-6 pt-6 border-t text-center">
            <p className="text-sm" style={{ color: brand.slate }}>
              Already have an account?{" "}
              <Link to="/login" className="font-semibold hover:underline" style={{ color: brand.navy }}>
                Log in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
